import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import Sidebar from './sidebar';

const Dashboard = () => {
  // State for sidebar toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [liveOccupancy, setLiveOccupancy] = useState({
    UGA: 0,
    UGB: 0,
    '1B': 0,
    '2A': 0,
    '2B': 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState({});
  // New state to track the current hour range
  const [currentHourRange, setCurrentHourRange] = useState([]);

  // Toggle sidebar function
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Refs for chart canvases - define them as objects that will hold the actual refs
  const chartRefs = {
    ugaOccupancy: useRef(null),
    ugaVisits: useRef(null),
    ugbOccupancy: useRef(null),
    ugbVisits: useRef(null),
    oneBVisits: useRef(null),
    twoAOccupancy: useRef(null),
    twoAVisits: useRef(null),
    twoBOccupancy: useRef(null),
    twoBVisits: useRef(null),
  };

  // Chart instances
  const chartInstances = useRef({});

  // Convert UTC hour to HKT hour (add 8 hours)
  const utcToHKT = (utcHour) => {
    return (utcHour + 8) % 24;
  };

  // Convert HKT hour to UTC hour (subtract 8 hours, handle negative values)
  const hktToUTC = (hktHour) => {
    return (hktHour - 8 + 24) % 24;
  };

  // Generate the range of hours from 8 AM to 8 PM (20:00) in HKT regardless of current time
const generateTodayHoursAxis = () => {
  // Always show hours from 8 AM to 8 PM (20:00)
  const startHour = 8;
  const endHour = 23;
  
  // Generate hours from 8 to 20
  const hours = [];
  for (let i = startHour; i <= endHour; i++) {
    hours.push(i.toString());
  }
  
  return hours;
};

  // Fetch live occupancy data
  const fetchLiveOccupancy = async () => {
    try {
      // Create an object to store occupancy data
      const occupancyByArea = {
        UGA: 0,
        UGB: 0,
        '1B': 0,
        '2A': 0,
        '2B': 0
      };
      
      // Fetch data for 1B from sensmax_live endpoint
      const sensmaxResponse = await fetch('https://cuhkmigration-dot-optimus-hk.df.r.appspot.com/sensemax-live-data');
      if (!sensmaxResponse.ok) {
        throw new Error('Failed to fetch sensmax live data');
      }
      const sensmaxData = await sensmaxResponse.json();
      
      // Process sensmax data for 1B
      const area1BData = sensmaxData.find(item => item.areaId === '1B');
      if (area1BData) {
        occupancyByArea['1B'] = area1BData.occupancy;
      }
      
      // Fetch data for other areas from hella_live endpoint
      const hellaResponse = await fetch('https://cuhkmigration-dot-optimus-hk.df.r.appspot.com/hella-live-data');
      if (!hellaResponse.ok) {
        throw new Error('Failed to fetch hella live data');
      }
      const hellaData = await hellaResponse.json();
      
      // Process hella data for other areas
      hellaData.forEach(item => {
        if (item.areaId && occupancyByArea.hasOwnProperty(item.areaId)) {
          occupancyByArea[item.areaId] = item.occupancy;
        }
      });
      
      setLiveOccupancy(occupancyByArea);
      return occupancyByArea;
    } catch (err) {
      console.error('Error fetching live occupancy:', err);
      setError('Failed to load live occupancy data');
      return null;
    }
  };

  // Get current date in HKT
  const getHKTDate = () => {
    const options = { timeZone: 'Asia/Hong_Kong' };
    return new Date(new Date().toLocaleString('en-US', options));
  };

  // Function to fetch trend data for a specific area
const fetchTrendData = async (areaId) => {
  let endpoint;
  
  // Updated API endpoints
  if (areaId === '1B') {
    endpoint = 'https://cuhkmigration-dot-optimus-hk.df.r.appspot.com/sensemax-trend';
  } else {
    endpoint = 'https://cuhkmigration-dot-optimus-hk.df.r.appspot.com/hella-trend';
  }
  
  try {
    // Create a URLSearchParams object for proper parameter encoding
    const params = new URLSearchParams();
    params.append('areaId', areaId);
    
    // Use the proper URL with parameters
    const url = `${endpoint}?${params.toString()}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch trend data for ${areaId}`);
    }
    const data = await response.json();
    
    // Add debugging console logs
    console.log(`Raw API data for ${areaId}:`, data);
    
    // Define the HKT hours we're interested in
    const startHktHour = 8;
    const endHktHour = 20;
    
    // Generate the HKT hour labels
    const hoursToDisplay = generateTodayHoursAxis();
    
    // Initialize arrays with zeros for each hour in the display range
    const occupancyData = Array(hoursToDisplay.length).fill(0);
    const visitsData = Array(hoursToDisplay.length).fill(0);

    // Only populate data for hours that have passed
    const currentHktHour = getHKTDate().getHours();
    
    // Filter the data to only include entries for the requested areaId
    const filteredData = Array.isArray(data) 
      ? data.filter(item => item.areaId === areaId)
      : [];
    
    console.log(`Filtered data for ${areaId}:`, filteredData);
    
    // Process the filtered data with additional logging
    if (filteredData.length > 0) {
      filteredData.forEach(item => {
        if (item.hasOwnProperty('hour')) {
          // Convert UTC hour from API to HKT
          const utcHour = parseInt(item.hour);
          const hktHour = utcToHKT(utcHour);
          
          console.log(`${areaId} - UTC hour: ${utcHour}, HKT hour: ${hktHour}`);
          console.log(`Item data:`, item);
          
          // Only process data for hours in our display range
          if (hktHour >= startHktHour && hktHour <= endHktHour && hktHour <= currentHktHour) {
            // Map to index in our arrays (0 for 8 AM, 1 for 9 AM, etc.)
            const arrayIndex = hktHour - startHktHour;
            
            console.log(`${areaId} - HKT hour ${hktHour} maps to index ${arrayIndex}`);
            
            // Process max_occupancy
            if (item.hasOwnProperty('max_occupancy')) {
              occupancyData[arrayIndex] = item.max_occupancy;
              console.log(`${areaId} - Setting occupancy[${arrayIndex}] = ${item.max_occupancy}`);
            }
            
            // Use total_in for visits as specified in requirements
            if (item.hasOwnProperty('total_in')) {
              visitsData[arrayIndex] = item.total_in;
              console.log(`${areaId} - Setting visits[${arrayIndex}] = ${item.total_in}`);
            }
          } else {
            console.log(`${areaId} - Hour ${hktHour} outside of display range (${startHktHour}-${endHktHour})`);
          }
        }
      });
    } else {
      console.log(`No data found for area ${areaId}`);
    }
    
    console.log(`Final ${areaId} occupancy data:`, occupancyData);
    console.log(`Final ${areaId} visits data:`, visitsData);
    
    return {
      occupancy: occupancyData,
      visits: visitsData
    };
  } catch (err) {
    console.error(`Error fetching trend data for ${areaId}:`, err);
    setError(`Failed to load trend data for ${areaId}`);
    return {
      occupancy: Array(generateTodayHoursAxis().length).fill(0),
      visits: Array(generateTodayHoursAxis().length).fill(0)
    };
  }
};

  // Function to create a chart with dynamic Y-axis scale
  const createChart = (canvasRef, labels, data, title, color, area, chartType) => {
    if (!canvasRef || !canvasRef.current) {
      console.error(`Canvas reference not found for ${title}`);
      return null;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      console.error(`Could not get 2D context for ${title}`);
      return null;
    }

    // Generate a unique ID for this chart if the canvas doesn't have an id
    const chartId = canvas.id || `chart-${Math.random().toString(36).substr(2, 9)}`;

    // If a chart already exists for this canvas, destroy it
    if (chartInstances.current[chartId]) {
      chartInstances.current[chartId].destroy();
    }

    // Find the max value in the data for dynamic scaling
    const maxValue = Math.max(...data);
    // Ensure there's always some headroom in the chart (20% extra space)
    const suggestedMax = maxValue * 1.2;

    // Set y-axis configuration for dynamic scaling
    let yAxisConfig = {
      beginAtZero: true,
      grid: {
        color: 'rgba(200, 200, 200, 0.2)'
      },
      ticks: {
        font: {
          size: 10
        }
      }
    };

    

    // Configure recommended step sizes based on chart type, but allow dynamic scaling
    if (chartType === 'occupancy') {
      switch (area) {
        case 'UGA':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(10, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 2  // Custom step size for UGA occupancy chart
            }
          };
          break;
        case 'UGB':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(20, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 5  // Custom step size for UGA occupancy chart
            }
          };
          break;
        case '2A':
        case '2B':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(10, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 2  // Custom step size for UGA occupancy chart
            }
          };
          break;
        default:
          yAxisConfig.suggestedMax = suggestedMax;
          break;
      }
    } else if (chartType === 'visits') {
      switch (area) {
        case 'UGA':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(50, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 10  // Custom step size for UGA visits chart
            }
          };
          break;
        case 'UGB':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(120, suggestedMax),

          };
          break;
        case '1B':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(150, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 20  // Custom step size for UGA visits chart
            }
          };
          break;
        case '2A':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(50, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 10  // Custom step size for UGA visits chart
            }
          };
          break;
        case '2B':
          yAxisConfig = {
            ...yAxisConfig,
            suggestedMax: Math.max(50, suggestedMax),
            ticks: {
              ...yAxisConfig.ticks,
              stepSize: 10  // Custom step size for UGA visits chart
            }
          };
          break;
        default:
          yAxisConfig.suggestedMax = suggestedMax;
          break;
      }
    }

    // Create new chart
    try {
      const newChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [{
            label: title,
            data: data,
            backgroundColor: color,
            borderColor: color.replace('0.6', '0.8'),
            borderWidth: 1,
            borderRadius: 2,
            maxBarThickness: 38,
            barPercentage: 0.8,
            categoryPercentage: 0.9
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: yAxisConfig,
            x: {
              grid: {
                display: false
              },
              ticks: {
                font: {
                  size: 10
                }
              }
            }
          },
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'center',
              labels: {
                boxWidth: 15,
                usePointStyle: true,
                pointStyle: 'circle',
                padding: 10,
                font: {
                  size: 12,
                  weight: 'bold'
                }
              }
            },
            tooltip: {
              mode: 'index',
              intersect: false,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              titleColor: '#666',
              bodyColor: '#000',
              borderColor: '#ddd',
              borderWidth: 1,
              padding: 10,
              cornerRadius: 4,
              displayColors: false,
              titleFont: {
                weight: 'bold'
              }
            }
          }
        }
      });
      
      chartInstances.current[chartId] = newChart;
      return newChart;
    } catch (err) {
      console.error(`Error creating chart for ${title}:`, err);
      return null;
    }
  };

  // Function to update chart data
  const updateCharts = async () => {
    try {
      // Generate the hours axis based on current time
      const hoursAxis = generateTodayHoursAxis();
      setCurrentHourRange(hoursAxis);
      
      // Fetch trend data for all areas in parallel
      const areas = ['UGA', 'UGB', '1B', '2A', '2B'];
      const trendDataPromises = areas.map(area => fetchTrendData(area));
      const allTrendData = await Promise.all(trendDataPromises);
      
      // Create an object to store the data for all areas
      const areaData = {};
      areas.forEach((area, index) => {
        areaData[area] = allTrendData[index];
      });
      
      // Store the data in state so the render can access it
      setChartData({
        hoursAxis,
        areaData
      });
      
      // Also update live occupancy
      await fetchLiveOccupancy();
      
      return { hoursAxis, areaData };
    } catch (err) {
      console.error('Error updating charts:', err);
      setError('Failed to update chart data');
      return null;
    }
  };

  // Fetch data and prepare it for charts
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      
      try {
        // Update charts with initial data
        await updateCharts();
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load dashboard data');
        setIsLoading(false);
      }
    };
    
    fetchAllData();
    
    // Set up interval to refresh data every 5 minutes (300000ms)
    // This will update both charts and live occupancy
    const chartUpdateInterval = setInterval(() => {
      updateCharts();
    }, 300000);
    
    // More frequent updates for live occupancy - every 50 seconds
    const liveOccupancyInterval = setInterval(() => {
      fetchLiveOccupancy();
    }, 50000);
    
    // Cleanup function
    return () => {
      clearInterval(chartUpdateInterval);
      clearInterval(liveOccupancyInterval);
      // Destroy all chart instances on unmount
      Object.values(chartInstances.current).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, []);
  
  // Create charts after render when canvas elements and data are available
  useEffect(() => {
    // Skip if still loading or if there's no chart data
    if (isLoading || !chartData.hoursAxis || !chartData.areaData) {
      return;
    }
    
    const { hoursAxis, areaData } = chartData;
    const areas = ['UGA', 'UGB', '1B', '2A', '2B'];
    
    // Create charts for each area
    areas.forEach(area => {
      const occupancyData = areaData[area]?.occupancy || Array(hoursAxis.length).fill(0);
      const visitsData = areaData[area]?.visits || Array(hoursAxis.length).fill(0);
      
      let occupancyRef, visitsRef;
      
      switch(area) {
        case 'UGA':
          occupancyRef = chartRefs.ugaOccupancy;
          visitsRef = chartRefs.ugaVisits;
          break;
        case 'UGB':
          occupancyRef = chartRefs.ugbOccupancy;
          visitsRef = chartRefs.ugbVisits;
          break;
        case '1B':
          occupancyRef = null; // No occupancy chart for 1B
          visitsRef = chartRefs.oneBVisits;
          break;
        case '2A':
          occupancyRef = chartRefs.twoAOccupancy;
          visitsRef = chartRefs.twoAVisits;
          break;
        case '2B':
          occupancyRef = chartRefs.twoBOccupancy;
          visitsRef = chartRefs.twoBVisits;
          break;
        default:
          break;
      }
      
      // Create occupancy chart (skip for 1B)
      if (area !== '1B' && occupancyRef && occupancyRef.current) {
        createChart(
          occupancyRef, 
          hoursAxis, 
          occupancyData, 
          'Max Occupancy', 
          'rgba(255, 215, 0, 0.6)', 
          area, 
          'occupancy'
        );
      }
      
      // Create visits chart
      if (visitsRef && visitsRef.current) {
        createChart(
          visitsRef, 
          hoursAxis, 
          visitsData, 
          'Total Visits', 
          'rgba(78, 158, 248, 0.6)', 
          area, 
          'visits'
        );
      }
    });
  }, [chartData, isLoading]);

  // Current date in HKT
  const hktDate = getHKTDate();
  const day = hktDate.getDate().toString().padStart(2, '0');
  const month = hktDate.toLocaleString('en-US', { month: 'short' });
  const year = hktDate.getFullYear(); 
  const weekday = hktDate.toLocaleString('en-US', { weekday: 'long' }); 
  
  const formattedDate = `${day} ${month} ${year}, ${weekday}`;

  // Function to determine background color based on occupancy
  const getOccupancyColor = (count, areaId) => {
    // Get max capacity based on area
    let maxCapacity;
    switch(areaId) {
      case 'UGA':
        maxCapacity = 20;
        break;
      case 'UGB':
        maxCapacity = 30;
        break;
      case '1B':
        maxCapacity = 15; // From the API response
        break;
      case '2A':
        maxCapacity = 40;
        break;
      case '2B':
        maxCapacity = 30;
        break;
      default:
        maxCapacity = 20;
    }
    
    // Calculate percentage of capacity
    const percentage = (count / maxCapacity) * 100;
    
    // Determine color based on percentage
    if (percentage >= 75) {
      return 'bg-[#E67A69]'; // Red for high occupancy
    } else if (percentage >= 50) {
      return 'bg-[#FFBB54]'; // Yellow/Orange for medium occupancy
    } else {
      return 'bg-[#7BC891]'; // Green for low occupancy
    }
  };

  // Add IDs to canvas elements
  const getCanvasId = (area, type) => `${area.toLowerCase().replace(/[^a-z0-9]/g, '')}-${type}`;

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      {/* Main Content */}
      <div className="flex-1 transition-all duration-300">
        {/* Top Navigation Bar */}
        <div className="bg-white p-6 shadow flex items-center relative">
          {/* Hamburger Menu */}
          <button 
            onClick={toggleSidebar}
            className="text-gray-600 hover:text-gray-900 focus:outline-none z-10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Centered Logo */}
          <div className="absolute left-0 right-0 mx-auto flex justify-center">
            <img 
              src="/download.png" 
              alt="CUHK INNOPORT" 
              className="h-12" 
            />
          </div>
        </div>
        
        {/* Content Area */}
        <div className="p-6">
          <div className="text-gray-700 font-semibold text-3xl mb-8">{formattedDate}</div>
          
          {/* Live Occupancy Section */}
          <div className="bg-white mx-12 rounded-xl border border-[#E2E2E4] shadow-[0_1px_2px_0_#dedede] p-6 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800 sm:justify-center">Live Occupancy</h2>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-4">{error}</div>
            ) : (
              <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:justify-center md:gap-8 items-center">
                <div className={`${getOccupancyColor(liveOccupancy.UGA, 'UGA')} rounded-md py-4 px-8 text-white text-center w-32`}>
                  <div className="text-xl font-semibold">UGA</div>
                  <div className="flex justify-center my-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="text-2xl ml-2 -mt-1 font-bold">{liveOccupancy.UGA}</div>
                  </div>
                </div>
                
                <div className={`${getOccupancyColor(liveOccupancy.UGB, 'UGB')} rounded-md py-4 px-6 text-white text-center w-32`}>
                  <div className="text-xl font-semibold">UGB</div>
                  <div className="flex justify-center my-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="text-2xl ml-2 -mt-1 font-bold">{liveOccupancy.UGB}</div>
                  </div>
                </div>
                
                <div className={`${getOccupancyColor(liveOccupancy['1B'], '1B')} rounded-md py-4 px-6 text-white text-center w-32`}>
                  <div className="text-xl font-semibold">1B</div>
                  <div className="flex justify-center my-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="text-2xl ml-2 -mt-1 font-bold">{liveOccupancy['1B']}</div>
                  </div>
                </div>
                
                <div className={`${getOccupancyColor(liveOccupancy['2A'], '2A')} rounded-md py-4 px-6 text-white text-center w-32`}>
                  <div className="text-xl font-semibold">2A</div>
                  <div className="flex justify-center my-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="text-2xl ml-2 -mt-1 font-bold">{liveOccupancy['2A']}</div>
                  </div>
                </div>
                
                <div className={`${getOccupancyColor(liveOccupancy['2B'], '2B')} rounded-md py-4 px-6 text-white text-center w-32`}>
                  <div className="text-xl font-semibold">2B</div>
                  <div className="flex justify-center my-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div className="text-2xl ml-2 -mt-1 font-bold">{liveOccupancy['2B']}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Display Current Time Range */}
          {/* <div className="bg-white mx-12 rounded-xl border border-[#E2E2E4] shadow-[0_1px_2px_0_#dedede] p-6 mb-8">
            <h2 className="text-3xl font-bold mb-6 text-gray-800">Today's Data</h2>
            <div className="text-center text-gray-700">
              {currentHourRange.length > 0 ? (
                <p className="text-lg">
                   Data from {currentHourRange[0]}:00 to {currentHourRange[currentHourRange.length-1]}:00 HKT
                </p>
              ) : (
                <p className="text-lg">Waiting for today's data...</p>
              )}
            </div>
          </div> */}
          
          {/* Occupancy Trends Section */}
          <div className="bg-white mx-12 rounded-xl border border-[#E2E2E4] shadow-[0_1px_2px_0_#dedede] p-6">
            <h2 className="text-3xl text-gray-800 font-bold mb-6">Occupancy Trends</h2>
            
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-4">{error}</div>
            ) : (
              <>
                {/* UGA Section */}
                <div className="mb-12">
                  <h3 className="text-2xl text-gray-700 font-extrabold mb-6">UGA</h3>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('UGA', 'occupancy')} ref={chartRefs.ugaOccupancy} height="250"></canvas>
                  </div>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('UGA', 'visits')} ref={chartRefs.ugaVisits} height="250"></canvas>
                  </div>
                </div>
                
                {/* UGB Section */}
                <div className="mb-12">
                  <h3 className="text-2xl text-gray-700 font-extrabold mb-6">UGB</h3>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('UGB', 'occupancy')} ref={chartRefs.ugbOccupancy} height="250"></canvas>
                  </div>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('UGB', 'visits')} ref={chartRefs.ugbVisits} height="250"></canvas>
                  </div>
                </div>
                
                {/* 1B Section - Only Visits Chart */}
                <div className="mb-12">
                  <h3 className="text-2xl text-gray-700 font-extrabold mb-6">1B</h3>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('1B', 'visits')} ref={chartRefs.oneBVisits} height="250"></canvas>
                  </div>
                </div>
                
                {/* 2A Section */}
                <div className="mb-12">
                  <h3 className="text-2xl text-gray-700 font-extrabold mb-6">2A</h3>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('2A', 'occupancy')} ref={chartRefs.twoAOccupancy} height="250"></canvas>
                  </div>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('2A', 'visits')} ref={chartRefs.twoAVisits} height="250"></canvas>
                  </div>
                </div>
                  
                {/* 2B Section */}
                <div>
                  <h3 className="text-2xl text-gray-700 font-extrabold mb-6">2B</h3>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('2B', 'occupancy')} ref={chartRefs.twoBOccupancy} height="250"></canvas>
                  </div>
                  <div className="h-64 mb-10 mx-8">
                    <canvas id={getCanvasId('2B', 'visits')} ref={chartRefs.twoBVisits} height="250"></canvas>
                  </div>
                </div>
              </>
            )}
          </div>
            
          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <div className='flex flex-row justify-center gap-2'>
              <p className="mb-1">Powered by Power Workplace</p> 
              <img src='/image.png' width={24} height={14} /> 
            </div>
            <p>Terms of Service</p>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  export default Dashboard;